import axios from 'axios';

// Clear stuck MSAL interaction state on page load
if (window.localStorage.getItem('msal.interaction.status') === 'interaction_in_progress') {
  window.localStorage.removeItem('msal.interaction.status');
}

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Google OAuth Configuration
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Microsoft OAuth Configuration
export const MICROSOFT_CLIENT_ID = process.env.REACT_APP_MICROSOFT_CLIENT_ID;
export const MICROSOFT_TENANT_ID = process.env.REACT_APP_MICROSOFT_TENANT_ID;

// Google OAuth Functions
export const initializeGoogleOAuth = () => {
  return new Promise((resolve, reject) => {
    if (window.google) {
      resolve(window.google);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.google) {
        resolve(window.google);
      } else {
        reject(new Error('Google OAuth failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Google OAuth script'));
    document.head.appendChild(script);
  });
};

export const handleGoogleLogin = async () => {
  try {
    const google = await initializeGoogleOAuth();
    
    return new Promise((resolve, reject) => {
      // Set up a timeout to prevent infinite loading
      const timeout = setTimeout(() => {
        reject(new Error('OAuth login timed out. Please try again.'));
      }, 60000); // 60 second timeout
      
      const tokenClient = google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        callback: async (response) => {
          clearTimeout(timeout);
          
          if (response.error) {
            // Handle specific error cases
            if (response.error === 'popup_closed_by_user') {
              reject(new Error('Login cancelled by user'));
            } else if (response.error === 'access_denied') {
              reject(new Error('Access denied by user'));
            } else if (response.error === 'popup_blocked') {
              reject(new Error('Popup blocked by browser. Please allow popups for this site and try again.'));
            } else if (response.error === 'popup_failed_to_open') {
              reject(new Error('Failed to open popup. Please check your browser settings and try again.'));
            } else {
              reject(new Error(response.error));
            }
            return;
          }
          
          try {
            // Send the access token to our backend
            const result = await axios.post(`${API_BASE_URL}/api/users/oauth/login`, {
              access_token: response.access_token,
              provider: 'google'
            });
            
            resolve(result.data);
          } catch (error) {
            reject(error);
          }
        }
      });
      
      // Request access token - Google OAuth handles popup internally
      tokenClient.requestAccessToken();
    });
  } catch (error) {
    throw error;
  }
};

// Microsoft OAuth Functions - Singleton MSAL instance
let msalInstance = null;
let msalLibrary = null;
let msalInitializationPromise = null;

export const initializeMicrosoftOAuth = () => {
  // Check if Microsoft OAuth is properly configured
  if (!MICROSOFT_CLIENT_ID) {
    console.error('Microsoft OAuth Client ID is not configured. Please check your environment variables.');
    throw new Error('Microsoft OAuth is not properly configured. Missing REACT_APP_MICROSOFT_CLIENT_ID.');
  }
  
  // Return existing promise if initialization is in progress
  if (msalInitializationPromise) {
    return msalInitializationPromise;
  }
  
  msalInitializationPromise = new Promise((resolve, reject) => {
    // If MSAL library is already loaded, resolve immediately
    if (window.msal && msalLibrary) {
      resolve({ msal: msalLibrary, instance: msalInstance });
      return;
    }

    // If MSAL library is loaded but instance not created
    if (window.msal) {
      msalLibrary = window.msal;
      const msalConfig = {
        auth: {
          clientId: MICROSOFT_CLIENT_ID,
          authority: `https://login.microsoftonline.com/${MICROSOFT_TENANT_ID || 'common'}`,
          redirectUri: window.location.origin,
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false,
        }
      };
      
      msalInstance = new msalLibrary.PublicClientApplication(msalConfig);
      
      // Initialize MSAL instance - this must be called before any other operations
      msalInstance.initialize().then(() => {
        resolve({ msal: msalLibrary, instance: msalInstance });
      }).catch((error) => {
        reject(error);
      });
      return;
    }

    // Load MSAL library
    const script = document.createElement('script');
    script.src = 'https://alcdn.msauth.net/browser/2.30.0/js/msal-browser.min.js';
    script.async = true;
    script.onload = () => {
      if (window.msal) {
        msalLibrary = window.msal;
        const msalConfig = {
          auth: {
            clientId: MICROSOFT_CLIENT_ID,
            authority: `https://login.microsoftonline.com/${MICROSOFT_TENANT_ID || 'common'}`,
            redirectUri: window.location.origin,
          },
          cache: {
            cacheLocation: 'sessionStorage',
            storeAuthStateInCookie: false,
          }
        };
        
        msalInstance = new msalLibrary.PublicClientApplication(msalConfig);
        
        // Initialize MSAL instance - this must be called before any other operations
        msalInstance.initialize().then(() => {
          resolve({ msal: msalLibrary, instance: msalInstance });
        }).catch((error) => {
          reject(error);
        });
      } else {
        reject(new Error('Microsoft OAuth failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Microsoft OAuth script'));
    document.head.appendChild(script);
  });
  
  return msalInitializationPromise;
};

/** Thrown when the app runs inside a popup/iframe; Microsoft login must run in a full tab. */
export const MICROSOFT_OPEN_IN_TAB = 'MICROSOFT_OPEN_IN_TAB';

function isInPopup() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.opener && !window.opener.closed) || window !== window.top;
}

export const handleMicrosoftLogin = async () => {
  // If we're inside a popup or iframe, MSAL will block (block_nested_popups). Redirect the top window to login so Microsoft auth runs in the main tab.
  if (isInPopup()) {
    const loginUrl = `${window.location.origin}/login?microsoft=1`;
    try {
      if (window.opener && !window.opener.closed) {
        window.opener.location.href = loginUrl;
        window.close();
        return null;
      }
      if (window !== window.top) {
        window.top.location.href = loginUrl;
        return null;
      }
    } catch (e) {
      // Cross-origin (e.g. Cursor embedded browser): can't redirect parent
      throw new Error(MICROSOFT_OPEN_IN_TAB);
    }
  }

  // Clear stale interaction state so redirect can start
  const interactionStatus = window.localStorage.getItem('msal.interaction.status');
  if (interactionStatus === 'interaction_in_progress') {
    const interactionStart = window.localStorage.getItem('msal.interaction.start');
    if (interactionStart) {
      const elapsed = Date.now() - parseInt(interactionStart, 10);
      if (elapsed > 5 * 60 * 1000) {
        window.localStorage.removeItem('msal.interaction.status');
        window.localStorage.removeItem('msal.interaction.start');
      } else {
        throw new Error('A Microsoft login is already in progress. Please complete it first.');
      }
    } else {
      window.localStorage.removeItem('msal.interaction.status');
    }
  }

  try {
    const { instance: msalInstance } = await initializeMicrosoftOAuth();
    await msalInstance.handleRedirectPromise();

    const loginRequest = {
      scopes: ['openid', 'profile', 'email', 'User.Read', 'Calendars.Read', 'offline_access']
    };

    // Use redirect by default – avoids popup blockers and works in all browsers
    await msalInstance.loginRedirect(loginRequest);
    return null; // User will complete login on Microsoft and land on /microsoft/callback
  } catch (error) {
    window.localStorage.removeItem('msal.interaction.status');
    window.localStorage.removeItem('msal.interaction.start');
    const msg = (error && error.message) ? String(error.message) : '';
    if (/block_nested_popups|running in a popup/i.test(msg)) {
      throw new Error(MICROSOFT_OPEN_IN_TAB);
    }
    console.error('Microsoft OAuth initialization failed:', error);
    throw error;
  }
};

// Handle Microsoft redirect (call this on app initialization)
export const handleMicrosoftRedirect = async () => {
  try {
    const { instance: msalInstance } = await initializeMicrosoftOAuth();
    
    // Handle redirect response
    const response = await msalInstance.handleRedirectPromise();
    
    if (response) {
      // Clear interaction state on successful redirect
      window.localStorage.removeItem('msal.interaction.status');
      window.localStorage.removeItem('msal.interaction.start');
      
      // Send the access token and refresh token to our backend
      const result = await axios.post(`${API_BASE_URL}/api/users/oauth/login`, {
        access_token: response.accessToken,
        refresh_token: response.refreshToken,
        provider: 'microsoft'
      });
      
      return result.data;
    }
    
    return null;
  } catch (error) {
    // Clear interaction state on error
    window.localStorage.removeItem('msal.interaction.status');
    window.localStorage.removeItem('msal.interaction.start');
    console.error('Microsoft redirect error:', error);
    return null;
  }
};

// Microsoft token refresh function
export const refreshMicrosoftToken = async () => {
  try {
    const { instance: msalInstance } = await initializeMicrosoftOAuth();
    
    // Handle any pending redirects first
    await msalInstance.handleRedirectPromise();
    
    // Get all accounts
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No Microsoft accounts found');
    }
    
    const account = accounts[0];
    
    // Silent token acquisition
    const response = await msalInstance.acquireTokenSilent({
      scopes: ['openid', 'profile', 'email', 'User.Read', 'Calendars.Read', 'offline_access'],
      account: account
    });
    
    // Update tokens in backend
    await axios.post(`${API_BASE_URL}/api/users/oauth/login`, {
      access_token: response.accessToken,
      refresh_token: response.refreshToken,
      provider: 'microsoft'
    });
    
    return response.accessToken;
  } catch (error) {
    console.error('Microsoft token refresh error:', error);
    throw error;
  }
}; 